import { useState } from "react";
import { KeyRound } from "lucide-react";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";
import { Page, Button, Badge, Alert, Card, DescriptionList, ResponsiveGrid, Text } from "@marekl/adminer/components";
import { ChangePasswordModal } from "../../components/ChangePasswordModal";
import { useAuth } from "../../auth/useAuth";
import type { Role } from "../../auth/queries";
import { formatDateTime } from "@marekl/adminer/format";
import styles from "./ProfilePage.module.css";

const ROLE_LABEL: Record<Role, string> = {
  reception: "Recepce",
  manager: "Správce",
  superadmin: "Superadmin",
};

const ROLE_VARIANT: Record<Role, "info" | "warn" | "err"> = {
  reception: "info",
  manager: "warn",
  superadmin: "err",
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function ProfilePage() {
  const { user } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);

  useSetTopbarCrumbs([{ label: "Můj profil" }]);

  if (!user) return null;
  const name = user.display_name || user.username;

  return (
    <>
      <Page.Content>
        <Page.Header
          title="Můj profil"
          subtitle="nastavení účtu a hesla"
          subtitleVariant="tagline"
        />

        <ResponsiveGrid gap="lg">
          <Card
            header={<Card.Header title="Účet" />}
          >
            <div className={styles.identity}>
              <div className={styles.avatar} aria-hidden="true">{initials(name)}</div>
              <div className={styles.identityText}>
                <div className={styles.name}>{name}</div>
                <Text variant={["mono", "muted"]}>@{user.username}</Text>
              </div>
            </div>

            <DescriptionList labelWidth={120}>
              <DescriptionList.Item label="Role"><Badge variant={ROLE_VARIANT[user.role]}>{ROLE_LABEL[user.role]}</Badge></DescriptionList.Item>
              <DescriptionList.Item label="E-mail">{user.email || <Text variant="muted">—</Text>}</DescriptionList.Item>
              <DescriptionList.Item label="Stav">
                  {user.active
                    ? <Badge variant="ok">Aktivní</Badge>
                    : <Badge variant="muted">Deaktivován</Badge>}
              </DescriptionList.Item>
              <DescriptionList.Item label="Posl. přihlášení">{user.last_login_at ? formatDateTime(user.last_login_at) : <Text variant="muted">—</Text>}</DescriptionList.Item>
            </DescriptionList>
          </Card>

          <Card header={<Card.Header title="Heslo" />}>
            {user.password_locked ? (
              <Alert variant="info" title="Změna hesla je zakázána">
                Heslo tohoto účtu je spravováno administrátorem a nelze jej samostatně měnit.
              </Alert>
            ) : (
              <div className={styles.pwBlock}>
                <p className={styles.pwHint}>
                  Z bezpečnostních důvodů heslo pravidelně měňte a nesdílejte ho s ostatními.
                </p>
                <Button variant="primary" onClick={() => setPwOpen(true)}>
                  <KeyRound size={16} aria-hidden="true" />
                  Změnit heslo
                </Button>
              </div>
            )}
          </Card>
        </ResponsiveGrid>
      </Page.Content>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </>
  );
}
